import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useState, useEffect, useRef, createContext, useContext, useCallback, useMemo } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import InstallPWA from "@/components/InstallPWA";

// Pages
import { LandingPage } from "@/pages/Landing";
import LandingHome from "@/pages/LandingHome";
import { AuthCallback } from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import NewRound from "@/pages/NewRound";
import CreateRound from "@/pages/CreateRound";
import PlayRound from "@/pages/PlayRound";
import PlayResults from "@/pages/PlayResults";
import SocialStudio from "@/pages/SocialStudio";
import Studio from "@/pages/Studio";
import RoundsHistory from "@/pages/RoundsHistory";
import Players from "@/pages/Players";
import CalculationPreview from "@/pages/CalculationPreview";
import HoleCapture from "@/pages/HoleCapture";
import LiveSummary from "@/pages/LiveSummary";
import RoundResults from "@/pages/RoundResults";
import RoundDetail from "@/pages/RoundDetail";
import DoublesConfig from "@/pages/DoublesConfig";
import FavoritePlayers from "@/pages/FavoritePlayers";
import Profile from "@/pages/Profile";


// Pages (kept)

// Admin Pages
import AdminLogin from "@/pages/AdminLogin";
import AdminLayout from "@/pages/AdminLayout";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminUsers from "@/pages/AdminUsers";
import AdminPlayers from "@/pages/AdminPlayers";
import AdminClubs from "@/pages/AdminClubs";
import AdminSettings from "@/pages/AdminSettings";
import AdminCourses from "@/pages/AdminCourses";
import AdminSocialTemplates from "@/pages/AdminSocialTemplates";

// Club Pages
import ClubLogin from "@/pages/ClubLogin";
import CadiihouseLanding from "@/pages/CadiihouseLanding";
import ClubLayout from "@/pages/ClubLayout";
import ClubDashboard from "@/pages/ClubDashboard";
import ClubPlaceholder from "@/pages/ClubPlaceholder";
import ClubTeam from "@/pages/ClubTeam";
import ClubPlayers from "@/pages/ClubPlayers";
import ClubTeeTimes from "@/pages/ClubTeeTimes";
import ClubCategories from "@/pages/ClubCategories";
import ClubRankings from "@/pages/ClubRankings";
import ClubTournaments from "@/pages/ClubTournaments";
import ClubLanding from "@/pages/ClubLanding";

// Public Microsite Pages
import ClubPublicPage from "@/pages/ClubPublicPage";
import PublicRankingPage from "@/pages/PublicRankingPage";
import PublicStagePage from "@/pages/PublicStagePage";
import PublicTournamentPage from "@/pages/PublicTournamentPage";
import TournamentBigScreenPage from "@/pages/TournamentBigScreenPage";
import { Privacy, Terms } from "@/pages/Legal";
import Support from "@/pages/Support";
import Contact from "@/pages/Contact";
import Marketing from "@/pages/Marketing";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `/api`;

// Global fetch interceptor: add Bearer token for API calls
const _originalFetch = window.fetch.bind(window);
window.fetch = function () {
  try {
    const resource = arguments[0];
    if (typeof resource === "string" && resource.includes("/api/")) {
      const token = localStorage.getItem("cadii_token");
      if (token && arguments.length >= 2 && arguments[1]) {
        const init = arguments[1];
        const existingHeaders = init.headers || {};
        // Check if Authorization already set
        let hasAuth = false;
        if (existingHeaders instanceof Headers) {
          hasAuth = existingHeaders.has("Authorization");
        } else if (typeof existingHeaders === "object") {
          hasAuth = "Authorization" in existingHeaders || "authorization" in existingHeaders;
        }
        if (!hasAuth) {
          // Build new headers without cloning the init object
          const newHeaders = {};
          if (existingHeaders instanceof Headers) {
            existingHeaders.forEach((v, k) => { newHeaders[k] = v; });
          } else {
            Object.keys(existingHeaders).forEach(k => { newHeaders[k] = existingHeaders[k]; });
          }
          newHeaders["Authorization"] = "Bearer " + token;
          return _originalFetch(resource, {
            method: init.method,
            headers: newHeaders,
            body: init.body,
            credentials: init.credentials,
            mode: init.mode,
            cache: init.cache,
            redirect: init.redirect,
            referrer: init.referrer,
            signal: init.signal,
          });
        }
      }
    }
  } catch (e) {
    // Silent fallback
  }
  return _originalFetch.apply(window, arguments);
};

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: "include",
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // Logout silent fail
    }
    localStorage.removeItem("cadii_token");
    setUser(null);
  }, []);

  const contextValue = useMemo(
    () => ({ user, setUser, loading, logout, checkAuth }),
    [user, loading, logout, checkAuth]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading, setUser, checkAuth } = useAuth();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const checkedRef = useRef(false);

  useEffect(() => {
    // If user data was passed from AuthCallback via location state, use it immediately
    if (location.state?.user && !user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    // If we already have a user from context, we're good
    if (user) {
      setIsAuthenticated(true);
      return;
    }

    // If global auth check is still loading, wait
    if (loading) return;

    // Check server-side auth (cookie validation)
    if (!checkedRef.current) {
      checkedRef.current = true;
      checkAuth().then(() => {
        // checkAuth sets the user in context if successful
        // Give a small window for state to propagate
        setTimeout(() => {
          setIsAuthenticated(prev => prev === null ? false : prev);
        }, 150);
      });
      return;
    }
  }, [location.state, user, loading, setUser, checkAuth]);

  // Update isAuthenticated when user changes
  useEffect(() => {
    if (user) setIsAuthenticated(true);
  }, [user]);

  if (loading || isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#64748B] font-medium">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// App Router Component
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingHome />} />
      <Route path="/login" element={<LandingPage />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/support" element={<Support />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/marketing" element={<Marketing />} />

      {/* Admin Panel */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="clubs" element={<AdminClubs />} />
        <Route path="courses" element={<AdminCourses />} />
        <Route path="social" element={<AdminSocialTemplates />} />
        <Route path="players" element={<AdminPlayers />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>

      {/* Club Panel */}
      <Route path="/club/login" element={<ClubLogin />} />
      <Route path="/cadiihouse" element={<CadiihouseLanding />} />
      <Route path="/club" element={<ClubLayout />}>
        <Route index element={<ClubDashboard />} />
        <Route path="players" element={<ClubPlayers />} />
        <Route path="tee-times" element={<ClubTeeTimes />} />
        <Route path="categories" element={<ClubCategories />} />
        <Route path="rankings" element={<ClubRankings />} />
        <Route path="competitions" element={<ClubTournaments />} />
        <Route path="landing" element={<ClubLanding />} />
        <Route path="team" element={<ClubTeam />} />
        <Route path="settings" element={<ClubPlaceholder section="settings" />} />
      </Route>

      {/* Protected User Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/new-round"
        element={
          <ProtectedRoute>
            <NewRound />
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-round"
        element={
          <ProtectedRoute>
            <CreateRound />
          </ProtectedRoute>
        }
      />
      <Route
        path="/play/:roundId"
        element={
          <ProtectedRoute>
            <PlayRound />
          </ProtectedRoute>
        }
      />
      <Route
        path="/play/:roundId/results"
        element={
          <ProtectedRoute>
            <PlayResults />
          </ProtectedRoute>
        }
      />
      <Route
        path="/social/:roundId"
        element={
          <ProtectedRoute>
            <SocialStudio />
          </ProtectedRoute>
        }
      />
      <Route
        path="/studio"
        element={
          <ProtectedRoute>
            <Studio />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rounds"
        element={
          <ProtectedRoute>
            <RoundsHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/round/:roundId/players"
        element={
          <ProtectedRoute>
            <Players />
          </ProtectedRoute>
        }
      />
      <Route
        path="/round/:roundId/preview"
        element={
          <ProtectedRoute>
            <CalculationPreview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/round/:roundId/play"
        element={
          <ProtectedRoute>
            <HoleCapture />
          </ProtectedRoute>
        }
      />
      <Route
        path="/round/:roundId/doubles"
        element={
          <ProtectedRoute>
            <DoublesConfig />
          </ProtectedRoute>
        }
      />
      <Route
        path="/round/:roundId/summary"
        element={
          <ProtectedRoute>
            <LiveSummary />
          </ProtectedRoute>
        }
      />
      <Route
        path="/round/:roundId/results"
        element={
          <ProtectedRoute>
            <RoundResults />
          </ProtectedRoute>
        }
      />
      <Route
        path="/round/:roundId"
        element={
          <ProtectedRoute>
            <RoundDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/players/favorites"
        element={
          <ProtectedRoute>
            <FavoritePlayers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />

      {/* Public Microsite Routes (must be last to avoid shadowing static routes) */}
      <Route path="/:slug" element={<ClubPublicPage />} />
      <Route path="/:slug/rankings/:rankingId" element={<PublicRankingPage />} />
      <Route path="/:slug/rankings/:rankingId/stages/:stageId" element={<PublicStagePage />} />
      <Route path="/:slug/tournaments/:tournamentId" element={<PublicTournamentPage />} />
      <Route path="/:slug/tournaments/:tournamentId/pantalla" element={<TournamentBigScreenPage />} />

    </Routes>
  );
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-center" richColors />
          <AppRouter />
          <InstallPWA />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
